import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Link,
} from '@mui/material';
import { SEO } from '../components/SEO/SEO';
import { brandColors } from '../theme';

export const PrivacyPage: React.FC = () => {
  return (
    <>
      <SEO
        title="Privacy Policy"
        description="HTL MarketCompass Privacy Policy. Learn how we collect, use, and protect your personal information and hotel market data."
      />
      
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: { xs: 3, md: 6 } }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 600,
              color: brandColors.indigo,
              mb: 1,
            }}
          >
            Privacy Policy
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>

          <Divider sx={{ mb: 4 }} />

          <Box sx={{ '& > *': { mb: 3 } }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                1. Introduction
              </Typography>
              <Typography variant="body1" paragraph>
                Barrett HTL Inc ("we," "our," or "us") operates the HTL MarketCompass Store 
                (the "Store"), an e-commerce platform for purchasing digital market intelligence 
                reports and hospitality industry data. This Privacy Policy explains how we collect, 
                use, disclose, and safeguard your information when you visit our Store and make 
                purchases.
              </Typography>
              <Typography variant="body1" paragraph>
                This policy complies with the General Data Protection Regulation (GDPR), California 
                Consumer Privacy Act (CCPA), and other applicable data protection laws. By using our 
                Store, you consent to the data practices described in this policy.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                2. Information We Collect
              </Typography>
              
              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                2.1 Information You Provide
              </Typography>
              <Typography variant="body1" paragraph>
                When you create an account, make a purchase, or contact us, we collect:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1">Name and contact information (email, phone number)</Typography>
                <Typography component="li" variant="body1">Billing address and payment information</Typography>
                <Typography component="li" variant="body1">Company name and business details</Typography>
                <Typography component="li" variant="body1">Account credentials and preferences</Typography>
                <Typography component="li" variant="body1">Purchase history and subscription details</Typography>
                <Typography component="li" variant="body1">Communications and support inquiries</Typography>
              </Box>

              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                2.2 Information Collected Automatically
              </Typography>
              <Typography variant="body1" paragraph>
                When you visit our Store, we automatically collect:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1">IP address and location data</Typography>
                <Typography component="li" variant="body1">Browser type and device information</Typography>
                <Typography component="li" variant="body1">Pages viewed and time spent on the Store</Typography>
                <Typography component="li" variant="body1">Referral sources and exit pages</Typography>
                <Typography component="li" variant="body1">Product interactions and search queries</Typography>
                <Typography component="li" variant="body1">Shopping cart contents and checkout behavior</Typography>
              </Box>

              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                2.3 Payment Information
              </Typography>
              <Typography variant="body1" paragraph>
                Payment processing is handled by secure third-party payment processors (such as Stripe). 
                We do not store complete credit card numbers or sensitive payment details on our servers. 
                We only retain transaction IDs and the last four digits of card numbers for reference purposes.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                3. How We Use Your Information
              </Typography>
              <Typography variant="body1" paragraph>
                We use the collected information to:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1">Process and fulfill your orders for digital reports</Typography>
                <Typography component="li" variant="body1">Manage your account and subscriptions</Typography>
                <Typography component="li" variant="body1">Send order confirmations and delivery notifications</Typography>
                <Typography component="li" variant="body1">Provide customer support and respond to inquiries</Typography>
                <Typography component="li" variant="body1">Send marketing communications (with your consent)</Typography>
                <Typography component="li" variant="body1">Personalize your shopping experience and recommendations</Typography>
                <Typography component="li" variant="body1">Analyze usage patterns to improve our Store and products</Typography>
                <Typography component="li" variant="body1">Prevent fraud and enhance security</Typography>
                <Typography component="li" variant="body1">Comply with legal obligations and enforce our terms</Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                4. Legal Basis for Processing (GDPR)
              </Typography>
              <Typography variant="body1" paragraph>
                For customers in the European Economic Area (EEA), we process personal data based on:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1">
                  <strong>Contract Performance:</strong> To fulfill orders and provide purchased digital reports
                </Typography>
                <Typography component="li" variant="body1">
                  <strong>Legitimate Interests:</strong> To improve our Store, prevent fraud, and conduct marketing
                </Typography>
                <Typography component="li" variant="body1">
                  <strong>Legal Compliance:</strong> To comply with tax, accounting, and other legal requirements
                </Typography>
                <Typography component="li" variant="body1">
                  <strong>Consent:</strong> For marketing communications and optional data processing
                </Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                5. Digital Product Delivery
              </Typography>
              <Typography variant="body1" paragraph>
                When you purchase digital reports from our Store:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1">We grant you access to download purchased reports</Typography>
                <Typography component="li" variant="body1">Download links are sent to your registered email address</Typography>
                <Typography component="li" variant="body1">We track download activity for security and support purposes</Typography>
                <Typography component="li" variant="body1">Access to purchased content is maintained in your account</Typography>
                <Typography component="li" variant="body1">Subscription products provide ongoing access during the subscription period</Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                6. Data Sharing and Disclosure
              </Typography>
              <Typography variant="body1" paragraph>
                We do not sell your personal information. We may share your data with:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1">
                  <strong>Payment Processors:</strong> To process transactions securely
                </Typography>
                <Typography component="li" variant="body1">
                  <strong>Analytics Providers:</strong> To understand Store usage and improve services
                </Typography>
                <Typography component="li" variant="body1">
                  <strong>Email Service Providers:</strong> To send transactional and marketing emails
                </Typography>
                <Typography component="li" variant="body1">
                  <strong>Cloud Storage Providers:</strong> To host and deliver digital products
                </Typography>
                <Typography component="li" variant="body1">
                  <strong>Legal Authorities:</strong> When required by law or to protect our rights
                </Typography>
                <Typography component="li" variant="body1">
                  <strong>Business Partners:</strong> With your consent for joint offerings
                </Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                7. Your Rights and Choices
              </Typography>
              
              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                7.1 GDPR Rights (EEA Customers)
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1">
                  <strong>Access:</strong> Request a copy of your personal data
                </Typography>
                <Typography component="li" variant="body1">
                  <strong>Rectification:</strong> Correct inaccurate or incomplete data
                </Typography>
                <Typography component="li" variant="body1">
                  <strong>Erasure:</strong> Request deletion of your personal data
                </Typography>
                <Typography component="li" variant="body1">
                  <strong>Restriction:</strong> Limit processing of your data
                </Typography>
                <Typography component="li" variant="body1">
                  <strong>Portability:</strong> Receive your data in a portable format
                </Typography>
                <Typography component="li" variant="body1">
                  <strong>Objection:</strong> Object to certain processing activities
                </Typography>
              </Box>

              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                7.2 CCPA Rights (California Residents)
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1">Right to know what personal information we collect</Typography>
                <Typography component="li" variant="body1">Right to delete personal information</Typography>
                <Typography component="li" variant="body1">Right to opt-out of the sale of personal information (we do not sell data)</Typography>
                <Typography component="li" variant="body1">Right to non-discrimination for exercising privacy rights</Typography>
              </Box>

              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                To exercise these rights, contact us at <Link href="mailto:legal@barretthtl.com">legal@barretthtl.com</Link>.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                8. Data Security
              </Typography>
              <Typography variant="body1" paragraph>
                We implement industry-standard security measures to protect your information:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1">SSL/TLS encryption for data transmission</Typography>
                <Typography component="li" variant="body1">Secure payment processing through PCI-compliant providers</Typography>
                <Typography component="li" variant="body1">Regular security audits and vulnerability assessments</Typography>
                <Typography component="li" variant="body1">Access controls and authentication requirements</Typography>
                <Typography component="li" variant="body1">Employee training on data protection</Typography>
              </Box>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                While we strive to protect your information, no online platform is 100% secure. 
                Please use strong passwords and protect your account credentials.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                9. Cookies and Tracking
              </Typography>
              <Typography variant="body1" paragraph>
                We use cookies and similar technologies to:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1">Maintain your shopping cart and session</Typography>
                <Typography component="li" variant="body1">Remember your preferences and settings</Typography>
                <Typography component="li" variant="body1">Analyze Store traffic and usage patterns</Typography>
                <Typography component="li" variant="body1">Provide personalized recommendations</Typography>
                <Typography component="li" variant="body1">Prevent fraud and enhance security</Typography>
              </Box>
              
              <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
                Types of Cookies We Use:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1">
                  <strong>Essential Cookies:</strong> Required for Store functionality
                </Typography>
                <Typography component="li" variant="body1">
                  <strong>Analytics Cookies:</strong> Help us understand Store usage
                </Typography>
                <Typography component="li" variant="body1">
                  <strong>Marketing Cookies:</strong> Used for targeted advertising (with consent)
                </Typography>
                <Typography component="li" variant="body1">
                  <strong>Preference Cookies:</strong> Remember your settings and choices
                </Typography>
              </Box>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                You can manage cookie preferences through your browser settings. Disabling certain 
                cookies may affect Store functionality.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                10. Marketing Communications
              </Typography>
              <Typography variant="body1" paragraph>
                With your consent, we may send you:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1">New product announcements and report releases</Typography>
                <Typography component="li" variant="body1">Special offers and promotions</Typography>
                <Typography component="li" variant="body1">Industry insights and newsletters</Typography>
                <Typography component="li" variant="body1">Survey invitations and feedback requests</Typography>
              </Box>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                You can opt-out of marketing emails at any time using the unsubscribe link in our 
                emails or by contacting us. Transactional emails (order confirmations, delivery 
                notifications) cannot be opted out of while you have an active account.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                11. Data Retention
              </Typography>
              <Typography variant="body1" paragraph>
                We retain your information for as long as necessary to:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1">Provide access to purchased digital products</Typography>
                <Typography component="li" variant="body1">Maintain records for tax and accounting purposes (typically 7 years)</Typography>
                <Typography component="li" variant="body1">Resolve disputes and enforce agreements</Typography>
                <Typography component="li" variant="body1">Comply with legal obligations</Typography>
              </Box>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                When you request account deletion, we will delete or anonymize your personal data, 
                except where retention is required by law or for legitimate business purposes.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                12. International Data Transfers
              </Typography>
              <Typography variant="body1" paragraph>
                Your information may be transferred to and processed in countries other than your 
                country of residence, including the United States. We ensure appropriate safeguards 
                are in place for international transfers, including:
              </Typography>
              <Box component="ul" sx={{ pl: 3 }}>
                <Typography component="li" variant="body1">Standard Contractual Clauses approved by the European Commission</Typography>
                <Typography component="li" variant="body1">Data processing agreements with service providers</Typography>
                <Typography component="li" variant="body1">Compliance with Privacy Shield principles where applicable</Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                13. Children's Privacy
              </Typography>
              <Typography variant="body1" paragraph>
                Our Store is not intended for individuals under the age of 18. We do not knowingly 
                collect personal information from children. If we become aware that we have collected 
                information from a child without parental consent, we will promptly delete it.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                14. Third-Party Links
              </Typography>
              <Typography variant="body1" paragraph>
                Our Store may contain links to third-party websites or services. We are not responsible 
                for the privacy practices of these third parties. We encourage you to review their 
                privacy policies before providing any personal information.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                15. Updates to This Policy
              </Typography>
              <Typography variant="body1" paragraph>
                We may update this Privacy Policy to reflect changes in our practices or applicable laws. 
                We will notify you of material changes by email or through a notice on our Store. Your 
                continued use of the Store after such changes constitutes acceptance of the updated policy.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
                16. Contact Information
              </Typography>
              <Typography variant="body1" paragraph>
                If you have questions, concerns, or requests regarding this Privacy Policy or our 
                data practices, please contact us at:
              </Typography>
              <Box sx={{ pl: 3 }}>
                <Typography variant="body1">
                  <strong>Barrett HTL Inc</strong>
                </Typography>
                <Typography variant="body1">
                  Email: <Link href="mailto:legal@barretthtl.com">legal@barretthtl.com</Link>
                </Typography>
                <Typography variant="body1">
                  Data Protection Officer: <Link href="mailto:dpo@barretthtl.com">dpo@barretthtl.com</Link>
                </Typography>
              </Box>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                You have the right to lodge a complaint with your local data protection supervisory 
                authority if you believe our processing of your personal data violates applicable law.
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </>
  );
};